import { RefObject, useMemo, useRef, useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Grid } from "antd";
import dayjs from "dayjs";
import { RootState } from "src/store";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import { simpleHash } from "src/utility";
import { useHotelsSearchObject } from "../../hooks/useHotelsSearch";
import useRestoreHotelTimer from "../../hooks/useRestoreHotelTimer";
import { withConditionalComponent } from "src/HOC/WithMobileComponent";
import { Hotel, StoredHotelBookingData } from "src/store/apis/Hotels";
import HotelBookingProvider from "./context/HotelBookingContext";
import HotelBookingWeb from "./layouts/HotelBooking.web";
import HotelBookingMobile from "./layouts/HotelBooking.mobile";
import { TabSection } from "./components/BookingTabs";

const HotelBookingComponent = withConditionalComponent(HotelBookingWeb, HotelBookingMobile);

export type HotelBookingSharedProps = {
  sections: TabSection[];
  overviewRef: RefObject<HTMLDivElement>;
  popularAmenitiesRef: RefObject<HTMLDivElement>;
  roomsRef: RefObject<HTMLDivElement>;
  amenitiesRef: RefObject<HTMLDivElement>;
  locationRef: RefObject<HTMLDivElement>;
  extraGuestCharges: number | null;
  handleProceed: (index: number) => void;
};

export default function HotelBooking() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hotelKey } = useParams();
  const { searchObject, notFound: searchNotFound } = useHotelsSearchObject();
  const [selectedRoomIndex, setSelectedRoomIndex] = useState<number | null>(null);
  const { startedAt, maxTime } = useSelector((state: RootState) => state.timer);

  const stored: StoredHotelBookingData | null = useMemo(() => {
    if (!hotelKey) return null;
    const json = localStorage.getItem(hotelKey);
    if (!json) return null;
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }, [hotelKey]);
  useRestoreHotelTimer(stored?._session);

  const hotel: Hotel | null = stored?.hotel ?? null;
  const pointsFactor = typeof stored?.pointsFactor === "number" ? stored.pointsFactor : 1;
  const sessionId = stored?.sessionId ?? "";

  const selectedBookingCode: string | null = useMemo(() => {
    if (selectedRoomIndex === null || !hotel) return null;
    return hotel.rooms[selectedRoomIndex]?.bookingCode ?? null;
  }, [selectedRoomIndex, hotel]);

  const numberOfNights = useMemo(() => {
    if (!searchObject?.checkin || !searchObject?.checkout) return 1;
    return Math.max(1, dayjs(searchObject.checkout).diff(dayjs(searchObject.checkin), "day"));
  }, [searchObject]);

  const contextValue = useMemo(
    () => ({
      hotel: hotel as Hotel,
      searchObject,
      selectedRoomIndex,
      setSelectedRoomIndex,
      selectedBookingCode,
      numberOfNights,
      pointsFactor,
      sessionId,
    }),
    [hotel, searchObject, selectedRoomIndex, selectedBookingCode, numberOfNights, pointsFactor, sessionId],
  );
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];

  const overviewRef = useRef<HTMLDivElement>(null);
  const popularAmenitiesRef = useRef<HTMLDivElement>(null);
  const roomsRef = useRef<HTMLDivElement>(null);
  const amenitiesRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);

  const sections: TabSection[] = useMemo(
    () => [
      { key: "overview", label: t("Hotels.booking.overview"), ref: overviewRef },
      { key: "popularAmenities", label: t("Hotels.booking.popularFacilities"), ref: popularAmenitiesRef },
      { key: "rooms", label: t("Hotels.booking.rooms"), ref: roomsRef },
      { key: "amenities", label: t("Hotels.booking.facilities"), ref: amenitiesRef },
      { key: "location", label: t("Hotels.booking.location"), ref: locationRef },
    ],
    [t],
  );

  const extraGuestCharges = useMemo(() => {
    if (!hotel) return null;
    const charges = hotel.rooms.map((r) => r.extraGuestCharges).filter((c): c is number => c !== null && c > 0);
    return charges.length ? Math.max(...charges) : null;
  }, [hotel]);

  const handleProceed = (index: number) => {
    if (!hotel) return;
    const proceedBookingCode = hotel.rooms[index]?.bookingCode ?? null;
    const bookingData = {
      ...contextValue,
      selectedRoomIndex: index,
      selectedBookingCode: proceedBookingCode,
      _session: { startedAt, maxTime },
    };
    const stringifiedObject = JSON.stringify(bookingData);
    const { hash } = simpleHash(stringifiedObject);
    localStorage.setItem(hash, stringifiedObject);
    navigate(`book/${hash}`);
  };

  if (searchNotFound || !hotel) return <Navigate to="/not-found" />;

  const sharedProps: HotelBookingSharedProps = {
    sections,
    overviewRef,
    popularAmenitiesRef,
    roomsRef,
    amenitiesRef,
    locationRef,
    extraGuestCharges,
    handleProceed,
  };

  return (
    <HotelBookingProvider value={contextValue}>
      <HotelBookingComponent trigger={isLargeScreen} props={sharedProps} />
    </HotelBookingProvider>
  );
}
